import { useTranslation } from 'react-i18next';
import { Section } from '@/components/Ui/Section';
import { SwitchRow } from '@/components/Ui/SwitchRow';
import { UiLink } from '@/components/Ui/UiLink';
import type { SectionProps } from './types';

export function ProtectionSection({
  form,
  save,
  data,
  method,
  hasMessages,
}: Omit<SectionProps, 'spec'>) {
  const { t } = useTranslation();
  const captcha = form.check_challenge;
  // Only a submission that carried the widget proves an own form has it.
  const unconfirmed =
    captcha &&
    method === 'own' &&
    (data.problems ? data.problems.some((p) => p.cause === 'captcha_unconfirmed') : !hasMessages);
  const flagged = data.summary?.spam_7d ?? 0;

  const setCaptcha = (next: boolean) =>
    void save(
      { check_challenge: next },
      t(next ? 'ui.turnedOn' : 'ui.turnedOff', { name: t('form.protection.captcha') })
    );
  const setSpam = (next: boolean) =>
    void save(
      { filter_spam: next },
      t(next ? 'ui.turnedOn' : 'ui.turnedOff', { name: t('form.protection.spam') })
    );

  const captchaHelp = !captcha
    ? t('form.protection.captchaOffHelp')
    : method === 'own'
      ? unconfirmed
        ? t('form.protection.captchaOwnPending')
        : t('form.protection.captchaOwnWorking')
      : t('form.protection.captchaHosted', { method: t(`form.site.method.${method}`) });

  return (
    <Section id="protection" title={t('form.protection.title')}>
      <SwitchRow
        label={t('form.protection.captcha')}
        checked={captcha}
        onChange={setCaptcha}
        onLabel={unconfirmed ? t('form.protection.captchaOnPending') : undefined}
        help={captchaHelp}
        changed={
          captcha
            ? undefined
            : { defaultLabel: t('ui.on').toLowerCase(), onReset: () => setCaptcha(true) }
        }
      />
      <SwitchRow
        label={t('form.protection.spam')}
        checked={form.filter_spam}
        onChange={setSpam}
        extra={
          form.filter_spam && flagged > 0 ? (
            <span>
              {t('form.protection.flagged', { count: flagged })} ·{' '}
              <UiLink to={`/account/messages?form=${encodeURIComponent(form.id)}&verdict=spam`}>
                {t('form.risk.review')}
              </UiLink>
            </span>
          ) : undefined
        }
        help={t('form.protection.spamHelp')}
        changed={
          form.filter_spam
            ? { defaultLabel: t('ui.off').toLowerCase(), onReset: () => setSpam(false) }
            : undefined
        }
      />
    </Section>
  );
}
